import React, { useState, useEffect } from 'react';
import { Download, X, CheckCircle, FileText, Key, CreditCard, Code, Lock, Paperclip, AlertCircle } from 'lucide-react';
import Button from './Button';
import './DataExportModal.css';

/**
 * 데이터 내보내기 모달
 * 패스워드, 신용카드, API키, 보안메모, 첨부파일 선택적 내보내기
 */
function DataExportModal({ isOpen, onClose, onExport, loading = false }) {
  const [selectedItems, setSelectedItems] = useState({
    passwords: true,
    creditCards: true,
    apiKeys: true,
    secureNotes: true,
    attachments: false
  });
  
  const [exportFormat, setExportFormat] = useState('json'); // json, csv
  const [includePasswords, setIncludePasswords] = useState(false);
  const [exportEncryption, setExportEncryption] = useState('master'); // master, plain
  const [masterPassword, setMasterPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [estimatedSize, setEstimatedSize] = useState('계산 중...');

  useEffect(() => {
    if (isOpen) {
      // 초기화
      setMasterPassword('');
      setConfirmPassword('');
      calculateEstimatedSize();
    }
  }, [isOpen]);

  useEffect(() => {
    // 마스터 패스워드 선택 시 JSON 강제 선택
    if (exportEncryption === 'master' && exportFormat === 'csv') {
      setExportFormat('json');
    }
    calculateEstimatedSize();
  }, [selectedItems, includePasswords, exportEncryption]);

  const calculateEstimatedSize = () => {
    // 임시 계산 로직 (실제로는 서비스에서 계산)
    let size = 0;
    if (selectedItems.passwords) size += 50; // KB
    if (selectedItems.creditCards) size += 20;
    if (selectedItems.apiKeys) size += 15;
    if (selectedItems.secureNotes) size += 30;
    if (selectedItems.attachments) size += 500; // 첨부파일은 크기가 클 수 있음
    
    setEstimatedSize(`약 ${size}KB`);
  };

  const handleItemToggle = (itemType) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemType]: !prev[itemType]
    }));
  };

  const handleExport = () => {
    if (!hasSelectedItems()) return;
    
    // 마스터 패스워드 검증
    if (exportEncryption === 'master') {
      if (!masterPassword || masterPassword.length < 8) {
        alert('마스터 패스워드는 최소 8자 이상이어야 합니다.');
        return;
      }
      if (masterPassword !== confirmPassword) {
        alert('패스워드 확인이 일치하지 않습니다.');
        return;
      }
    }
    
    const exportConfig = {
      items: selectedItems,
      format: exportFormat,
      includePasswords,
      encryption: exportEncryption,
      masterPassword: exportEncryption === 'master' ? masterPassword : null,
      timestamp: new Date().toISOString()
    };
    
    onExport(exportConfig);
  };

  const hasSelectedItems = () => {
    return Object.values(selectedItems).some(selected => selected);
  };

  const getSelectedCount = () => {
    return Object.values(selectedItems).filter(selected => selected).length;
  };

  if (!isOpen) return null;

  const exportItems = [
    {
      id: 'passwords',
      label: '패스워드',
      icon: Key,
      description: '저장된 모든 패스워드 항목',
      color: '#4f46e5',
      estimated: '약 50개 항목'
    },
    {
      id: 'creditCards',
      label: '신용카드',
      icon: CreditCard,
      description: '저장된 신용카드 정보',
      color: '#059669',
      estimated: '약 5개 항목'
    },
    {
      id: 'apiKeys',
      label: 'API 키',
      icon: Code,
      description: '저장된 API 키 및 토큰',
      color: '#dc2626',
      estimated: '약 10개 항목'
    },
    {
      id: 'secureNotes',
      label: '보안 메모',
      icon: Lock,
      description: 'PIN 보호 메모 및 내용',
      color: '#7c3aed',
      estimated: '약 15개 항목'
    },
    {
      id: 'attachments',
      label: '첨부파일',
      icon: Paperclip,
      description: '보안 메모의 첨부파일들',
      color: '#ea580c',
      estimated: '약 20개 파일',
      warning: '첨부파일은 용량이 클 수 있습니다'
    }
  ];

  return (
    <div className="export-modal-overlay">
      <div className="export-modal">
        <div className="export-modal-header">
          <div className="header-content">
            <Download size={24} className="export-icon" />
            <h2>데이터 내보내기</h2>
          </div>
          <button 
            className="close-button" 
            onClick={onClose}
            disabled={loading}
          >
            <X size={18} />
          </button>
        </div>

        <div className="export-modal-body">
          <div className="export-description">
            <p>내보낼 데이터를 선택하세요. 선택된 데이터는 암호화되어 안전하게 다운로드됩니다.</p>
          </div>

          {/* 데이터 선택 */}
          <div className="export-items-section">
            <h3>내보낼 데이터 선택</h3>
            <div className="export-items-grid">
              {exportItems.map((item) => {
                const Icon = item.icon;
                const isSelected = selectedItems[item.id];
                
                return (
                  <div
                    key={item.id}
                    className={`export-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleItemToggle(item.id)}
                  >
                    <div className="export-item-header">
                      <div className="export-item-icon" style={{ color: item.color }}>
                        <Icon size={20} />
                      </div>
                      <div className="export-item-info">
                        <h4>{item.label}</h4>
                        <p className="item-description">{item.description}</p>
                        <p className="item-estimated">{item.estimated}</p>
                        {item.warning && (
                          <p className="item-warning">
                            <AlertCircle size={14} />
                            {item.warning}
                          </p>
                        )}
                      </div>
                      <div className="export-item-checkbox">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleItemToggle(item.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="checkmark">
                          {isSelected && <CheckCircle size={16} />}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 내보내기 옵션 */}
          <div className="export-options-section">
            <h3>내보내기 옵션</h3>
            
            <div className="option-group">
              <label className="option-label">파일 형식</label>
              <div className="format-options">
                <label className="format-option">
                  <input
                    type="radio"
                    name="format"
                    value="json"
                    checked={exportFormat === 'json'}
                    onChange={(e) => setExportFormat(e.target.value)}
                  />
                  <span>JSON</span>
                  <small>구조화된 데이터, 가져오기 지원</small>
                </label>
                <label className={`format-option ${exportEncryption === 'master' ? 'disabled' : ''}`}>
                  <input
                    type="radio"
                    name="format"
                    value="csv"
                    checked={exportFormat === 'csv'}
                    onChange={(e) => setExportFormat(e.target.value)}
                    disabled={exportEncryption === 'master'}
                  />
                  <span>CSV</span>
                  <small>
                    스프레드시트 호환, 첨부파일 제외
                    {exportEncryption === 'master' && <br />}
                    {exportEncryption === 'master' && <em>(마스터 패스워드 암호화 불가)</em>}
                  </small>
                </label>
              </div>
            </div>

            <div className="option-group">
              <label className="option-label">암호화 방식</label>
              <div className="format-options">
                <label className="format-option">
                  <input
                    type="radio"
                    name="encryption"
                    value="master"
                    checked={exportEncryption === 'master'}
                    onChange={(e) => setExportEncryption(e.target.value)}
                  />
                  <span>마스터 패스워드</span>
                  <small>별도 패스워드로 암호화, 어디서든 복원 가능</small>
                </label>
                <label className="format-option">
                  <input
                    type="radio"
                    name="encryption"
                    value="plain"
                    checked={exportEncryption === 'plain'}
                    onChange={(e) => setExportEncryption(e.target.value)}
                  />
                  <span>암호화 없음</span>
                  <small>평문 저장, 높은 호환성 (⚠️ 보안 위험)</small>
                </label>
              </div>
            </div>

            {exportEncryption === 'master' && (
              <div className="master-password-section">
                <h4>마스터 패스워드 설정</h4>
                <p className="master-password-description">
                  내보낸 파일을 암호화할 패스워드를 설정하세요. 이 패스워드는 데이터를 가져올 때 필요합니다.
                </p>
                <div className="password-inputs">
                  <div className="password-input-group">
                    <label>마스터 패스워드</label>
                    <input
                      type="password"
                      value={masterPassword}
                      onChange={(e) => setMasterPassword(e.target.value)}
                      placeholder="최소 8자 이상"
                      className="master-password-input"
                    />
                  </div>
                  <div className="password-input-group">
                    <label>패스워드 확인</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="패스워드를 다시 입력"
                      className="master-password-input"
                    />
                  </div>
                </div>
                {masterPassword && confirmPassword && masterPassword !== confirmPassword && (
                  <div className="password-error">패스워드가 일치하지 않습니다.</div>
                )}
                {masterPassword && masterPassword.length < 8 && (
                  <div className="password-error">패스워드는 최소 8자 이상이어야 합니다.</div>
                )}
              </div>
            )}

            <div className="option-group">
              <label className="option-checkbox">
                <input
                  type="checkbox"
                  checked={includePasswords}
                  onChange={(e) => setIncludePasswords(e.target.checked)}
                />
                <span className="checkbox-text">
                  실제 패스워드 포함
                  <small>체크하지 않으면 패스워드는 ****로 표시됩니다</small>
                </span>
              </label>
            </div>
          </div>

          {/* 요약 정보 */}
          <div className="export-summary">
            <div className="summary-item">
              <span>선택된 항목</span>
              <strong>{getSelectedCount()}개 카테고리</strong>
            </div>
            <div className="summary-item">
              <span>예상 크기</span>
              <strong>{estimatedSize}</strong>
            </div>
            <div className="summary-item">
              <span>파일 형식</span>
              <strong>{exportFormat.toUpperCase()}</strong>
            </div>
          </div>

          {/* 보안 안내 */}
          <div className="security-notice">
            <AlertCircle size={16} />
            <div className="notice-text">
              <strong>보안 안내</strong>
              <p>내보낸 데이터는 암호화되어 저장됩니다. 파일을 안전한 곳에 보관하고, 불필요 시 삭제하세요.</p>
            </div>
          </div>
        </div>

        <div className="export-modal-footer">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={!hasSelectedItems() || loading}
            loading={loading}
          >
            <Download size={16} />
            {loading ? '내보내는 중...' : '내보내기'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default DataExportModal;